import {Button, Input, Link} from "@heroui/react";
import {useMutation} from '@tanstack/react-query';
import repositories from '../../repositories.jsx';
import {useForm} from '@mantine/form';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';

export default function ForgotPassword() {
	const navigate = useNavigate();

	const form = useForm({
		initialValues: {
			email: '',
		},
	});
	const onSubmit = useMutation({
		mutationFn: () => repositories.auth.forgotPassword(form.values),
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong');
		},
	});

	const submit = () => {
		localStorage.removeItem('token');
		onSubmit.mutate();
	};
	const onEnter = (e) => {
		if (e.key === 'Enter') submit();
	};

	const inputClassNames = {
		base: 'group',
		label: 'text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 group-data-[focus=true]:text-primary group-data-[filled-within=true]:text-primary',
		inputWrapper: 'h-[58px] rounded-[14px] bg-[#f8f7ff] border-[1.5px] border-[#e0d9f7] shadow-none ' +
			'data-[hover=true]:bg-[#f8f7ff] ' +
			'group-data-[focus=true]:!bg-white group-data-[focus=true]:!border-primary ' +
			'group-data-[focus=true]:shadow-[0_0_0_3px_rgba(90,10,224,0.10)] transition-all',
		input: 'text-sm text-slate-900',
	};

	const mailIcon = (
		<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
			<path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
		</svg>
	);
	const backIcon = (
		<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
			<path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
		</svg>
	);

	// ── Success state ──
	if (onSubmit.isSuccess) {
		return (
			<div className="text-center py-4">
				<div className="auth-pop-in w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 text-white"
				     style={{background: 'linear-gradient(135deg, #5A0AE0, #8C2CE2)', boxShadow: '0 8px 28px rgba(90,10,224,0.35)'}}>
					<svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
						<path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
					</svg>
				</div>
				<h2 className="text-2xl font-black text-slate-900 mb-2">Check your inbox.</h2>
				<p className="text-sm text-slate-500 mb-1">We sent a reset link to</p>
				<p className="text-sm font-bold text-primary mb-6 break-all">{form.values.email}</p>
				<p className="text-xs text-slate-400 mb-8">
					Didn&apos;t receive it? Check spam or{' '}
					<button onClick={() => onSubmit.reset()}
					        className="text-primary hover:underline font-semibold">try again</button>.
				</p>
				<Button
					onPress={() => navigate('/login')}
					disableRipple
					startContent={backIcon}
					className="w-full h-auto py-3.5 rounded-2xl border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all">
					Back to sign in
				</Button>
			</div>
		);
	}

	// ── Form state ──
	return (
		<>
			<button onClick={() => navigate('/login')}
			        className="auth-fade-1 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 font-medium mb-8 transition-colors">
				{backIcon}
				Back to sign in
			</button>

			<div className="auth-fade-1 w-12 h-12 rounded-2xl flex items-center justify-center mb-6"
			     style={{background: 'linear-gradient(135deg, rgba(90,10,224,0.12), rgba(140,44,226,0.12))', border: '1.5px solid rgba(90,10,224,0.2)'}}>
				<svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
					<path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
				</svg>
			</div>

			<h1 className="auth-fade-1 font-sans text-[32px] font-black text-slate-900 leading-tight tracking-tight mb-1">
				Reset password.
			</h1>
			<p className="auth-fade-1 text-sm text-slate-500 mb-8">
				Enter your email and we&apos;ll send a reset link.
			</p>

			<div className="auth-fade-2 mb-6">
				<Input
					label="Email address"
					labelPlacement="inside"
					variant="flat"
					autoComplete="email"
					onKeyDown={onEnter}
					{...form.getInputProps('email')}
					classNames={inputClassNames}
				/>
			</div>

			<Button
				isLoading={onSubmit.isPending}
				disableRipple
				onPress={submit}
				className="auth-fade-2 auth-btn-wrap w-full h-auto py-3.5 mb-5 rounded-2xl text-white text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
				style={{background: 'linear-gradient(to right, #5A0AE0, #8C2CE2)', boxShadow: '0 6px 20px rgba(90,10,224,0.3)'}}
				startContent={!onSubmit.isPending && mailIcon}>
				<span className="auth-shimmer"/>
				Send reset link
			</Button>

			<p className="auth-fade-3 text-center text-xs text-slate-400">
				Remember your password?{' '}
				<Link href="/login" className="text-primary hover:underline font-semibold cursor-pointer">Sign in</Link>
			</p>
		</>
	);
}
